/**
 * SeparateExplainer — progressive-disclosure explainer for SEPARATE (S).
 *
 * SEPARATE neutralizes the vulnerability class "data is interpreted as
 * instruction" by placing data flow and instruction flow on the two
 * orthogonal axes of a double category. The 2-cell that would let a data
 * morphism act as an instruction morphism is empty (the seam), so the
 * confusion is structurally inexpressible.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { SEPARATE_META } from "@csk/core";
import { THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import {
  ExplainerCard,
  DoubleCategoryDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { TheoremMetaStrip } from "./TheoremMetaStrip.js";
import { TIER_ONE_IMMUNITY_LINK } from "./tierOneCrossLinks.js";

const ACCENT = THEOREM_COLOR.SEPARATE;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-separate",
  kind: "SSM · Neutralization Theorem (S1)",
  title: "SEPARATE",
  subtitle: "S · Double category · IMMUNE · G2 Knowledge",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Double category (structure)", href: "#explainer-double-category", accent: ACCENT },
  {
    label: "DATA.ORIGIN (G2 sibling)",
    href: "#explainer-data-origin",
    accent: THEOREM_COLOR["DATA.ORIGIN"],
  },
  {
    label: "DATA.REACH (G2 sibling)",
    href: "#explainer-data-reach",
    accent: THEOREM_COLOR["DATA.REACH"],
  },
  TIER_ONE_IMMUNITY_LINK,
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>SEPARATE</strong> says: data and instructions live on different axes and never swap,
      by construction. "Treat this input as code" is not a sentence the system can form.
    </p>
  ),
  intuition: (
    <>
      <p>
        Injection is the oldest bug in the book: a string of data crosses into the instruction plane
        and is executed. The usual answer is sanitization — scrub the data so it cannot be read as
        code. That is a story about cleaning the bytes, and it fails whenever the scrubber misses a
        case.
      </p>
      <p>
        CSK separates the two concerns into the horizontal (data) and vertical (instruction) axes of
        a double category. To turn data into instruction you would need a 2-cell between the axes —
        and that 2-cell is empty. The confusion is unconstructable in the source rather than
        scrubbed at runtime.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Model the system as a double category with horizontal morphisms (data transport) and
        vertical morphisms (instruction transport). A square is a 2-cell relating a horizontal and a
        vertical boundary; it exists only when an explicit, governed conversion is supplied.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        The seam is the empty square: there is no 2-cell carrying a pure data morphism into the
        instruction plane. The injection morphism is therefore not a closed term.{" "}
        <code>SEPARATE</code> is a projection of the Categorical Immunity Theorem, and pairs with
        the <code>PARTITION</code> SDP (parameterized self-dual).
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Vulnerability class neutralized:</strong> data–instruction confusion — code
        injection, XSS, SQL injection (CWE-94, CWE-79, CWE-89; e.g., CVE-2021-44228, CVE-2017-5638).
        Realized by <em>structural inexpressibility</em>: the data→instruction 2-cell is empty in
        the kernel double category.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If you hand a string to a host <code>eval</code>{" "}
        outside the double category, you have left the kernel. SEPARATE is then vacuous on that call
        — the seam has gone away, not been bypassed.
      </p>
      <p>
        <strong>Seam type</strong> (per <code>theory/structural/SEAM-THEORY.md</code>):{" "}
        {SEPARATE_META.seamType}. <strong>Bridge</strong>: authorized type conversion (governance
        artifact).
      </p>
    </>
  ),
  references: [
    {
      label: "SEPARATE theorem card",
      path: "docs/reference/theorem-cards/SEPARATE.md",
    },
    {
      label: "Kernel module (main theorem SEPARATE-neutralized)",
      path: `agda/${SEPARATE_META.agdaSource}`,
      note: `SEPARATE_META.agdaSource is "${SEPARATE_META.agdaSource}"; the repo path is prefixed with "agda/". Main theorem in module lines 64–310.`,
    },
    {
      label: "Theorem Canon (§4.1 IMMUNE; §7 PARTITION SDP)",
      path: "docs/reference/Theorem-Canon.md",
    },
  ],
};

const DIAGRAM = (
  <>
    <TheoremMetaStrip
      theoremName="SEPARATE"
      code="S"
      structure="Double category"
      mode="immune"
      generator="G2"
      seamType={SEPARATE_META.seamType}
      bridgeArtifact="authorized type conversion"
      theoremColor={ACCENT}
      washColor={withAlpha(ACCENT, "1A")}
    />
    <div style={{ marginTop: "0.5rem" }}>
      <DoubleCategoryDiagram
        accent={ACCENT}
        ariaLabel="Double category — left square has a filled 2-cell (governed conversion); the right SEPARATE seam square is empty"
      />
    </div>
  </>
);

export function SeparateExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
